import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { signOutRequest } from "../../redux/auth/auth.actions";
import { toastify } from "../../configs/utils";

// Styled Components
const TitleBuilderContainer = styled.div`
  padding: 2rem;
  width: 100%;
  background: #ededed;
  min-height: 100%;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const ContentCard = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 2.5rem;
  border: 1px solid rgba(17, 24, 39, 0.08);
`;

const TitleInputContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 48px;
  border: 1px solid ${(props) => (props.isDragging ? "#3b82f6" : "#d1d5db")};
  border-radius: 8px;
  background: transparent;
  padding: 0.75rem;
  transition: border-color 0.2s ease;
  cursor: text;
`;

const TitleInput = styled.div`
  width: 100%;
  min-height: auto;
  font-size: 1rem;
  color: #1f2937;
  line-height: 2;
  outline: none;
  border: none;
  background: transparent;
  font-family: inherit;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 0.25rem 0;

  &[contenteditable="true"]:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    font-style: italic;
    pointer-events: none;
  }

  .field-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    color: #374151;
    font-weight: 500;
    font-size: 0.875rem;
    margin: 0 0.125rem;
    user-select: none;
    cursor: default;
    contenteditable: false;

    .field-badge-remove {
      background: transparent;
      border: none;
      color: #ef4444;
      cursor: pointer;
      padding: 0;
      font-size: 0.875rem;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.15s ease;
      margin-left: 0.125rem;

      &:hover {
        color: #dc2626;
      }
    }
  }
`;

const AvailableFieldsSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #d1d5db;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: #111827;
`;

const AvailableFieldsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FieldItem = styled.div`
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  cursor: move;
  display: flex;
  align-items: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  user-select: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;

  &:hover {
    background: #e5e7eb;
    border-color: rgba(0, 0, 0, 0.4);
  }

  &.dragging {
    opacity: 0.5;
  }
`;

const PreviewSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #d1d5db;
`;

const PreviewTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: #111827;
`;

const PreviewBox = styled.div`
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 1.5rem;
  min-height: 100px;
  color: #111827;
  font-size: 1rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  cursor: not-allowed;

  .field-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    color: #374151;
    font-weight: 500;
    font-size: 0.875rem;
    margin: 0 0.125rem;

    .field-badge-remove {
      background: transparent;
      border: none;
      color: #ef4444;
      cursor: pointer;
      padding: 0;
      font-size: 0.875rem;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.15s ease;
      margin-left: 0.125rem;

      &:hover {
        color: #dc2626;
      }
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #d1d5db;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;

  background: #3b82f6;
  color: white;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:active:not(:disabled) {
    background: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.secondary {
    background: white;
    color: #374151;

    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  }
`;

const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 1rem 0 0 0;
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
`;

const SuccessMessage = styled.p`
  color: #059669;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 1rem 0 0 0;
  padding: 0.75rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
`;

function TitleBuilder() {
  const dispatch = useDispatch();
  const [previewHTML, setPreviewHTML] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [draggedField, setDraggedField] = useState(null);
  const [isDraggingOverInput, setIsDraggingOverInput] = useState(false);
  const titleInputRef = React.useRef(null);
  const [isTitleBuilderReady, setIsTitleBuilderReady] = useState(false);
  const [availableFields] = useState([
    "Card Name",
    "Type",
    "Manufacturer",
    "Set",
    "Card Number",
    "Year Manufactured",
  ]);

  const DEFAULT_BASE_URL = "http://localhost:8000";
  const API_BASE_URL =
    import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL;

  // Token refresh helper
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  // Rebuild contentEditable from configuration array
  const rebuildFromConfiguration = (configuration) => {
    if (!titleInputRef.current || !Array.isArray(configuration)) return;

    // Clear existing content
    titleInputRef.current.innerHTML = "";

    // Rebuild content from configuration
    configuration.forEach((item) => {
      if (item.type === "string") {
        const textNode = document.createTextNode(item.value || "");
        titleInputRef.current.appendChild(textNode);
      } else if (item.type === "field-name") {
        const fieldBadge = document.createElement("span");
        fieldBadge.className = "field-badge";
        fieldBadge.setAttribute("contenteditable", "false");
        fieldBadge.setAttribute("data-field", item.value || "");

        const fieldNameSpan = document.createElement("span");
        fieldNameSpan.textContent = item.value || "";
        fieldBadge.appendChild(fieldNameSpan);

        const removeButton = document.createElement("button");
        removeButton.className = "field-badge-remove";
        removeButton.textContent = "×";
        removeButton.setAttribute("contenteditable", "false");
        removeButton.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          removeFieldBadge(fieldBadge);
        };
        fieldBadge.appendChild(removeButton);

        titleInputRef.current.appendChild(fieldBadge);
        
        // Add a space after the field badge
        const spaceNode = document.createTextNode(" ");
        titleInputRef.current.appendChild(spaceNode);
      }
    });

    updateTitlePreview();
  };

  const [savedConfiguration, setSavedConfiguration] = useState(null);

  // Load title builder configuration
  useEffect(() => {
    const loadTitleBuilder = async () => {
      setError("");
      setSuccessMessage("");
      setIsTitleBuilderReady(false);

      try {
        let token = localStorage.getItem("access");
        if (!token) token = await refreshAccessToken();

        let response = await fetch(
          `${API_BASE_URL}/api/v1/cards/title-builder/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 401) {
          const newToken = await refreshAccessToken();
          response = await fetch(
            `${API_BASE_URL}/api/v1/cards/title-builder/`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${newToken}`,
                "Content-Type": "application/json",
              },
            }
          );
        }

        if (response.ok) {
          const data = await response.json();
          if (data.configuration && Array.isArray(data.configuration)) {
            // Use requestAnimationFrame to ensure DOM is ready, then rebuild
            requestAnimationFrame(() => {
              setTimeout(() => {
                rebuildFromConfiguration(data.configuration);
                setSavedConfiguration(data.configuration);
                // Mark as ready after a small delay to ensure all handlers are attached
                setTimeout(() => {
                  setIsTitleBuilderReady(true);
                  // Focus after rebuild is complete
                  requestAnimationFrame(() => {
                    setTimeout(() => {
                      if (titleInputRef.current) {
                        titleInputRef.current.focus();
                        const selection = window.getSelection();
                        const range = document.createRange();
                        range.selectNodeContents(titleInputRef.current);
                        range.collapse(false);
                        selection.removeAllRanges();
                        selection.addRange(range);
                      }
                    }, 50);
                  });
                }, 50);
              }, 100);
            });
          } else {
            // No configuration, but still mark as ready
            requestAnimationFrame(() => {
              setTimeout(() => {
                setIsTitleBuilderReady(true);
              }, 50);
            });
          }
        } else if (response.status === 404) {
          // No configuration saved yet, mark as ready
          requestAnimationFrame(() => {
            setTimeout(() => {
              setIsTitleBuilderReady(true);
            }, 50);
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(
            errorData.detail ||
              errorData.message ||
              `Failed to load configuration: ${response.statusText}`
          );
          // Still mark as ready even on error
          requestAnimationFrame(() => {
            setTimeout(() => {
              setIsTitleBuilderReady(true);
            }, 50);
          });
        }
      } catch (err) {
        console.error("Error fetching title builder configuration:", err);
        // Mark as ready even on error
        requestAnimationFrame(() => {
          setTimeout(() => {
            setIsTitleBuilderReady(true);
          }, 50);
        });
      }
    };

    loadTitleBuilder();
  }, [API_BASE_URL]);

  // Auto-focus input field when component is ready (only if no configuration was loaded)
  useEffect(() => {
    if (isTitleBuilderReady && titleInputRef.current && !savedConfiguration) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (titleInputRef.current) {
            titleInputRef.current.focus();
            // Place cursor at the end of content if there's any
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(titleInputRef.current);
            range.collapse(false); // Collapse to end
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 100);
      });
    }
  }, [isTitleBuilderReady, savedConfiguration]);

  // Remove field badge
  const removeFieldBadge = (badgeElement) => {
    if (!badgeElement || !titleInputRef.current) return;

    const textNode = document.createTextNode("");
    badgeElement.parentNode.replaceChild(textNode, badgeElement);

    const selection = window.getSelection();
    const range = document.createRange();
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    updateTitlePreview();
  };

  // Insert field at cursor position
  const insertFieldAtCursor = (fieldName) => {
    if (!titleInputRef.current) return;

    const selection = window.getSelection();
    let range;

    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(titleInputRef.current);
      range.collapse(false);
    }

    const fieldBadge = document.createElement("span");
    fieldBadge.className = "field-badge";
    fieldBadge.setAttribute("contenteditable", "false");
    fieldBadge.setAttribute("data-field", fieldName);

    const fieldNameSpan = document.createElement("span");
    fieldNameSpan.textContent = fieldName;
    fieldBadge.appendChild(fieldNameSpan);

    const removeButton = document.createElement("button");
    removeButton.className = "field-badge-remove";
    removeButton.textContent = "×";
    removeButton.setAttribute("contenteditable", "false");
    removeButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      removeFieldBadge(fieldBadge);
    };
    fieldBadge.appendChild(removeButton);

    range.deleteContents();
    range.insertNode(fieldBadge);

    // Add a space after the field badge
    const spaceNode = document.createTextNode(" ");
    range.setStartAfter(fieldBadge);
    range.insertNode(spaceNode);

    range.setStartAfter(spaceNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    updateTitlePreview();
  };

  // Example values for preview
  const getExampleValue = (fieldName) => {
    const examples = {
      "Card Name": "Pikachu",
      Type: "Electric",
      Manufacturer: "Pokemon",
      Set: "Base Set",
      "Card Number": "25",
      "Year Manufactured": "1999",
    };
    return examples[fieldName] || fieldName;
  };

  // Generate example preview HTML
  const generatePreview = () => {
    if (!titleInputRef.current) {
      return "Your title preview will appear here...";
    }

    const configuration = formatConfiguration();
    if (configuration.length === 0) {
      return "Your title preview will appear here...";
    }

    let previewText = "";
    configuration.forEach((item) => {
      if (item.type === "string") {
        previewText += item.value;
      } else if (item.type === "field-name") {
        const exampleValue = getExampleValue(item.value);
        previewText += exampleValue;
      }
    });

    const escapedText = previewText
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

    return escapedText || "Your title preview will appear here...";
  };

  // Update preview
  const updateTitlePreview = () => {
    if (!titleInputRef.current) {
      setPreviewHTML("");
      return;
    }

    setPreviewHTML(generatePreview());
  };

  // Handle input
  const handleInput = () => {
    updateTitlePreview();
  };

  // Handle click to ensure cursor stays where user clicks
  const handleClick = (e) => {
    // Let the browser handle the click naturally
    // Don't interfere with cursor position
    e.stopPropagation();
  };

  // Handle drag over (only on input field)
  const handleDragOver = (e) => {
    if (!isTitleBuilderReady) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDraggingOverInput(true);
  };

  // Handle drag leave (only on input field)
  const handleDragLeave = (e) => {
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;

    // Don't change state if we're moving to a child element (like a field badge)
    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }

    setIsDraggingOverInput(false);
  };

  // Handle drop (only on input field)
  const handleDrop = (e) => {
    if (!isTitleBuilderReady) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverInput(false);

    const field = e.dataTransfer.getData("text/plain");
    if (field) {
      insertFieldAtCursor(field);
    }
  };

  // Prevent drops on container (only allow on input itself)
  const handleContainerDragOver = (e) => {
    if (!isTitleBuilderReady) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // Don't set isDraggingOverInput here - only on the input itself
  };

  const handleContainerDrop = (e) => {
    if (!isTitleBuilderReady) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // Prevent drop on container - only allow on input
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    const selection = window.getSelection();
    if (selection.rangeCount) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      updateTitlePreview();
    }
  };

  // Clear all content
  const handleClear = () => {
    if (titleInputRef.current) {
      titleInputRef.current.innerHTML = "";
      setPreviewHTML("");
      setError("");
      setSuccessMessage("");
    }
  };

  // Format configuration for API
  const formatConfiguration = () => {
    if (!titleInputRef.current) return [];

    const configuration = [];

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text) {
          configuration.push({
            type: "string",
            value: text,
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList && node.classList.contains("field-badge")) {
          const fieldName = node.getAttribute("data-field");
          if (fieldName) {
            configuration.push({
              type: "field-name",
              value: fieldName,
            });
          }
        } else {
          for (let child = node.firstChild; child; child = child.nextSibling) {
            processNode(child);
          }
        }
      }
    };

    for (
      let child = titleInputRef.current.firstChild;
      child;
      child = child.nextSibling
    ) {
      processNode(child);
    }

    return configuration;
  };

  // Save settings
  const handleSaveSettings = async () => {
    const configuration = formatConfiguration();

    if (configuration.length === 0) {
      setError("Please add at least one field to save the configuration.");
      setSuccessMessage("");
      return;
    }

    setIsSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const payload = {
        configuration: configuration,
      };

      let response = await fetch(
        `${API_BASE_URL}/api/v1/cards/title-builder/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(`${API_BASE_URL}/api/v1/cards/title-builder/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        setSuccessMessage("Title builder settings saved successfully!");
        setError("");
        toast.success("Title builder settings saved successfully!");
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.detail ||
          errorData.message ||
          `Failed to save settings: ${response.statusText}`;
        setError(errorMessage);
        setSuccessMessage("");
        toast.error(errorMessage);
      }
    } catch (err) {
      const errorMessage =
        err.message || "Failed to save settings. Please try again.";
      setError(errorMessage);
      setSuccessMessage("");
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <TitleBuilderContainer>
      <ContentCard>
        <TitleInputContainer
          className="title-input-container"
          isDragging={isDraggingOverInput && isTitleBuilderReady}
          onDragOver={handleContainerDragOver}
          onDrop={handleContainerDrop}
          style={{
            opacity: isTitleBuilderReady ? 1 : 0.7,
            pointerEvents: isTitleBuilderReady ? "auto" : "none",
          }}
        >
          <TitleInput
            ref={titleInputRef}
            contentEditable={isTitleBuilderReady}
            onInput={handleInput}
            onClick={handleClick}
            onPaste={handlePaste}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-placeholder="Type your title here or drag fields from below..."
            suppressContentEditableWarning
            style={{
              pointerEvents: isTitleBuilderReady ? "auto" : "none",
            }}
          />
        </TitleInputContainer>

        <AvailableFieldsSection>
          <SectionTitle>Available Fields</SectionTitle>
          <AvailableFieldsList>
            {availableFields.map((field) => (
              <FieldItem
                key={field}
                draggable
                onDragStart={(e) => {
                  setDraggedField(field);
                  e.dataTransfer.setData("text/plain", field);
                  e.dataTransfer.effectAllowed = "copy";
                  if (e.target) {
                    e.target.style.opacity = "0.5";
                  }
                }}
                onDragEnd={(e) => {
                  setDraggedField(null);
                  if (e.target) {
                    e.target.style.opacity = "1";
                  }
                }}
                className={draggedField === field ? "dragging" : ""}
              >
                {field}
              </FieldItem>
            ))}
          </AvailableFieldsList>
        </AvailableFieldsSection>

        <PreviewSection>
          <PreviewTitle>Preview</PreviewTitle>
          <PreviewBox
            dangerouslySetInnerHTML={{
              __html: previewHTML || "Your title preview will appear here...",
            }}
          />
        </PreviewSection>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

        <ButtonGroup>
          <Button
            onClick={handleClear}
            className="secondary"
            disabled={isSaving}
          >
            Clear All
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={isSaving || formatConfiguration().length === 0}
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
        </ButtonGroup>
      </ContentCard>
    </TitleBuilderContainer>
  );
}

export default TitleBuilder;
